import React from "react";
import { motion } from "framer-motion";
import {
  Bath,
  BrickWall,
  Cable,
  DoorOpen,
  Droplets,
  Hammer,
  Home,
  Layers,
  Lightbulb,
  PaintRoller,
  PhoneCall,
  ShieldCheck,
} from "lucide-react";

const specifications = [
  {
    title: "Foundation & Structure",
    icon: Home,
    points: ["R.C.C framed structure to withstand seismic Zone-II."],
  },
  {
    title: "Super Structure",
    icon: BrickWall,
    points: ["Quality red bricks in cement mortar."],
  },
  {
    title: "Plastering",
    icon: Hammer,
    points: ["Double coat sponge finished plastering to internal and external walls."],
  },
  {
    title: "Doors & Windows",
    icon: DoorOpen,
    points: [
      "Main door: B.T wood door frame and shutter with aesthetically designed melamine polishing.",
      "Internal doors: B.T wood frame and solid core flush shutter with both sides veneer.",
      "Toilet doors: B.T wood frame and solid core flush shutter with both sides veneer.",
      "Windows: UPVC sliding windows system with mosquito net provision.",
      "Air-conditioning: split A/C provision for all bedrooms and halls.",
    ],
  },
  {
    title: "Paint",
    icon: PaintRoller,
    points: [
      "Internal: smooth finish with luppum over a coat of primer and top finish with two coats of plastic emulsion paint.",
      "Ceiling: smooth finish with luppum over a coat of primer and top finish with two coats of plastic emulsion paint.",
      "External: premium exterior texture emulsion paint.",
    ],
  },
  {
    title: "Flooring",
    icon: Layers,
    points: [
      "Drawing, dining, kitchen, living, foyer and staircase: best quality vitrified tiles with 4 inch skirting.",
      "All bedrooms: best quality wooden flooring.",
      "Toilets and utility area: best quality acid-resistant, anti-skid ceramic tiles.",
      "Covered terrace and balconies: best quality rustic series ceramic tiles.",
    ],
  },
  {
    title: "Tile Cladding & Dadoing",
    icon: Droplets,
    points: [
      "Kitchen platform: glazed ceramic tile dado up to 2'-0 inch height above kitchen platform.",
      "Toilets: designer make glazed ceramic tile dado up to 7'-0 inch height.",
      "Utility and wash: glazed ceramic tile dado up to 3'-3 inch height.",
      "Kitchen: granite platform with stainless steel sink, drain board, Aquaguard provision, exhaust fan and chimney provision.",
      "Water supply and sanitary systems: water supply points in kitchen and toilets with required utility provisions.",
    ],
  },
  {
    title: "Toilets",
    icon: Bath,
    points: [
      "Wash basin with hot and cold basin mixer valve.",
      "EWC with health faucet.",
      "Hot and cold wall mixer with shower.",
      "Provision for geyser in all toilets.",
      "All C.P fittings are chrome plated of Jaquar or reputed make.",
    ],
  },
  {
    title: "Electrical System",
    icon: Lightbulb,
    points: [
      "Concealed copper wiring in conduits for lights, fans, plug and power points wherever necessary.",
      "Power outlets for air-conditioners in all bedrooms and geysers in all bathrooms.",
      "Power plug for cooking range, refrigerator, microwave ovens, mixer and grinder in kitchen.",
      "Plug points for refrigerators, TV and audio systems where necessary.",
      "Miniature Circuit Breakers and ELCB for distribution board of standard make.",
      "All electrical fittings of MK, Northwest, Legrand or equivalent make.",
    ],
  },
  {
    title: "Security Network",
    icon: ShieldCheck,
    points: [
      "Gated community planning with access-controlled security support.",
      "Provision for video door phone and intercom connectivity.",
    ],
  },
  {
    title: "Communication Network",
    icon: PhoneCall,
    points: [
      "Telephone and TV points in bedrooms, drawing and dining areas.",
      "Telephone cable with high speed CAT 6 cable provision.",
      "Provision for DTH TV service.",
      "Video phone and intercom facility in all units connecting to security and amenities.",
      "Internet provision in master bedroom using CAT 6 cable.",
      "Wi-Fi enabled provision.",
    ],
  },
  {
    title: "Cable & Data",
    icon: Cable,
    points: [
      "Structured cable provision for voice, data and entertainment points.",
      "Concealed routing planned for clean interior finishes.",
    ],
  },
];

export default function VillasSpecification() {
  return (
    <section className="villas-spec-section" id="serene-specifications">
      <div className="container">
        <motion.header
          className="villas-spec-header"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.4 }}
        >
          <span className="villas-spec-kicker">Specification Schedule</span>
          <h2>Vessella Villas Specifications</h2>
          <p>
            Premium structure, finishes, services, and home-ready provisions
            shaped for a refined villa lifestyle.
          </p>
        </motion.header>

        <div className="villas-spec-grid">
          {specifications.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.article
                className="villas-spec-card"
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.25, delay: index * 0.02 }}
              >
                <div className="villas-spec-card-top">
                  <span className="villas-spec-icon" aria-hidden="true">
                    <Icon size={28} strokeWidth={1.8} />
                  </span>
                  <span className="villas-spec-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3>{item.title}</h3>

                <ul>
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
